import { getRoutePath, Link, useLocation } from "@/lib/simple-router";
import { FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { Mail, Phone, MapPin } from "lucide-react";
import superhostLogo from "@assets/airbnb-superhost-white.webp";
import qualmarkLogo from "@assets/qualmark-edited-for-web.webp";
import bookabachLogo from "@assets/bookabach-2024-cropped-transparent.webp";
import bookingAwardLogo from "@assets/booking-traveller-review-2026-white.webp";

// Retained for restoring the former footer badge:
// asset: @assets/Powered by Stripe - white.png; alt: Powered by Stripe
// className: h-5 w-auto shrink-0 mt-2 ml-1; width: 88; height: 20.

export default function Footer() {
  const [location, setLocation] = useLocation();

  return (
    <footer className="relative">
      {/* Wave separator */}
      <div className="relative bg-background">

        <svg 
          className="w-full h-12 md:h-16 lg:h-20" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
          style={{ transform: 'translateY(1px)' }}
        >
          {/* First wave - main brand color */}
          <path 
            d="M0,0 C150,60 350,60 600,30 C850,0 1050,60 1200,30 L1200,120 L0,120 Z" 
            fill="#AD6433"
            className="drop-shadow-sm"
          />
          {/* Second wave - lighter shade, more parallel and skinnier */}
          <path 
            d="M0,20 C150,80 350,80 600,50 C850,20 1050,80 1200,50 L1200,65 C1050,105 850,105 600,85 C350,105 150,105 0,85 Z" 
            fill="#C17A4A"
            opacity="0.7"
          />
        </svg>
      </div>
      {/* Footer content */}
      <div className="bg-[#AD6433] text-white py-16" style={{ marginTop: '-1px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-[30%_14%_28%_28%] gap-0">
          <div>
            <h3 className="mb-6 text-white" style={{ fontFamily: 'Georgia', fontStyle: 'italic', fontSize: '20px' }}>About</h3>
            
            <p className="text-white mb-6" style={{ fontFamily: 'Lato', fontWeight: '300' }}>
              Welcome to Toka Ridge! We provide self-service accommodation with a friendly team nearby who are happy to assist in any way. Toka Ridge was designed, fitted and artfully created by friends and family. The space is intended as a place to escape and connect with those special to you.
            </p>
            
            {/* Tourism accreditation and hosting recognition */}
            <div className="flex items-start gap-[10px] mb-6">
              <a
                href="https://www.qualmark.co.nz/"
                target="_blank"
                rel="noopener noreferrer"
                className="block shrink-0 -mt-1.5 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              >
                <img
                  src={qualmarkLogo}
                  alt="Qualmark five-star Gold Sustainable Tourism Business Award"
                  className="w-[60.48px] h-auto"
                  width={204}
                  height={266}
                  loading="lazy"
                />
              </a>
              <img
                src={bookabachLogo}
                alt="Bookabach Bach of the Year 2024"
                className="w-[61.66872px] h-auto shrink-0 rounded"
                width={232}
                height={259}
                loading="lazy"
              />
              <img
                src={bookingAwardLogo}
                alt="Booking.com Traveller Review Awards 2026 — 9.6 out of 10"
                className="w-[68.757444px] h-auto shrink-0"
                width={207}
                height={201}
                loading="lazy"
              />
              <img
                src={superhostLogo}
                alt="Airbnb Superhost"
                className="w-[71.333936091px] h-auto shrink-0 mt-[19px]"
                width={215}
                height={85}
                loading="lazy"
              />
            </div>
          </div>
          
          {/* Empty spacing column */}
          <div className="hidden md:block"></div>
          
          <div className="pl-[0px] pr-[0px]">
            <h3 className="mb-6 text-white whitespace-nowrap" style={{ fontFamily: 'Georgia', fontStyle: 'italic', fontSize: '20px' }}>Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="/" onClick={(e) => { e.preventDefault(); if (getRoutePath(location) === '/') { window.scrollTo({ top: 0, behavior: 'smooth' }); } else { setLocation('/', 'top'); } }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Home</a></li>
              <li><Link href="/villas" className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Villas</Link></li>
              <li><Link href="/amenities-experience" className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Amenities</Link></li>
              <li><Link href="/amenities-experience#guest-reviews" onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                setLocation('/amenities-experience#guest-reviews', 'guest-reviews');
              }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Reviews</Link></li>
              <li><Link href="/amenities-experience#reels-gallery" onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                setLocation('/amenities-experience#reels-gallery', 'reels-gallery');
              }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Reels &amp; Gallery</Link></li>
              <li><Link href="/events-weddings" className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Events & Weddings</Link></li>
              <li><Link href="/guest-info" className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Guest Info</Link></li>
              <li><a href="/blog" onClick={(e) => { e.preventDefault(); setLocation('/blog', 'top'); }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Blog</a></li>
              <li><Link href="/contact" className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Contact</Link></li>
              <li><a href="/terms" onClick={(e) => { e.preventDefault(); setLocation('/terms', 'top'); }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Terms and conditions</a></li>
              <li><a href="/terms" onClick={(e) => { e.preventDefault(); setLocation('/terms', 'privacy-policy-section'); }} className="text-white hover:text-gray-300 transition-colors whitespace-nowrap font-light cursor-pointer">Privacy Policy</a></li>
            </ul>
          </div>
          
          <div className="mt-8 md:mt-0">
            <h3 className="mb-6 text-white" style={{ fontFamily: 'Georgia', fontStyle: 'italic', fontSize: '20px' }}>Contact</h3>
            <ul className="space-y-3">
              <li>
                <a href="mailto:info@tokaridge.co.nz" className="text-white hover:text-gray-300 transition-colors flex items-center space-x-2 font-light">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span>info@tokaridge.co.nz</span>
                </a>
              </li>
              <li>
                <a href="tel:+6473460090" className="text-white hover:text-gray-300 transition-colors flex items-center space-x-2 font-light">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>+64 7 346 0090</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://maps.google.com/maps?q=Toka+Ridge+Rotorua" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-300 transition-colors flex items-start space-x-2 font-light"
                >
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-tight">512A Hamurana Road,<br className="hidden sm:inline" /> Rotorua, New Zealand, 3097</span>
                </a>
              </li>
            </ul>
            
            {/* Social media icons */}
            <div className="flex space-x-4 mt-6">
              <a 
                href="https://www.facebook.com/tokaridgenz/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 transition-colors"
              >
                <span className="sr-only">Facebook</span>
                <FaFacebook className="h-6 w-6" />
              </a>
              <a 
                href="https://www.instagram.com/tokaridgenz/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 transition-colors"
              >
                <span className="sr-only">Instagram</span>
                <FaInstagram className="h-6 w-6" />
              </a>
              <a 
                href="https://www.youtube.com/@TokaRidge" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 transition-colors"
              >
                <span className="sr-only">YouTube</span>
                <FaYoutube className="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>
        
        </div>
      </div>
      {/* Copyright section at absolute bottom */}
      <div className="text-white py-4 bg-[#AD6433]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center border-t border-[#C17A4A]/70 pt-4">
            <p className="text-white text-[12px]" style={{ fontFamily: 'Lato', fontWeight: '300' }}>
              © 2024 Toka Ridge. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
